import { useEffect, useMemo, useState } from 'react';
import { OneFlowError } from '../../core/errors';
import { formatBytes } from '../../core/format';
import { Priority } from '../../core/types';
import type { MediaKind } from '../../core/types';
import { engine } from '../../state/store';
import {
  decodeText,
  parseCsv,
  parseMarkdown,
  prettyJson,
  xmlWarnings,
  type Block,
  type Inline,
} from '../../viewers/text';
import { useSession } from '../hooks/useSession';
import { StatusStage } from './StatusStage';

const PAGE = 1024 * 1024;

/**
 * Text-like files preview from their first megabyte while the rest downloads.
 * Everything is rendered as inert text nodes: no HTML is ever injected. AC-013
 */
export function TextStage({ id, kind }: { id: string; kind: MediaKind }) {
  const { total } = useSession(id);
  const [limit, setLimit] = useState(PAGE);
  const [text, setText] = useState<string | null>(null);
  const [encoding, setEncoding] = useState('UTF-8');
  const [error, setError] = useState<string | null>(null);
  const [wrap, setWrap] = useState(true);

  useEffect(() => {
    const handle = engine.handle(id);
    if (!handle || total === null) return;
    const ac = new AbortController();
    const end = Math.min(total, limit);
    setError(null);
    handle
      .ensure(
        { start: 0, end },
        { priority: Priority.ImminentPlayback, reason: 'text-preview', signal: ac.signal },
      )
      .then((bytes) => {
        const d = decodeText(bytes, { truncated: end < total });
        setText(d.text);
        setEncoding(d.encoding);
      })
      .catch((e) => {
        if (ac.signal.aborted) return;
        setError(e instanceof OneFlowError ? e.appError.summary : 'The preview stopped.');
      });
    return () => ac.abort();
  }, [id, total, limit]);

  const more = total !== null && limit < total;
  if (total === 0)
    return (
      <StatusStage kind={kind} title="This file is empty" body="There is nothing to preview." />
    );
  if (error) return <StatusStage kind={kind} title={error} body="The download is unaffected." />;
  if (text === null) return <StatusStage kind={kind} title="Reading the file…" busy />;

  return (
    <div className="viewer text-viewer">
      <div className="viewer__bar">
        <span className="viewer__readout mono">{encoding}</span>
        {total !== null && (
          <span className="viewer__readout mono">
            {more ? `First ${formatBytes(limit)} of ${formatBytes(total)}` : formatBytes(total)}
          </span>
        )}
        {(kind === 'text' || kind === 'xml' || kind === 'json') && (
          <button
            type="button"
            className="btn btn--quiet"
            aria-pressed={wrap}
            onClick={() => setWrap((w) => !w)}
          >
            {wrap ? 'Unwrap lines' : 'Wrap lines'}
          </button>
        )}
      </div>
      <div className="text-viewer__scroll" tabIndex={0} role="region" aria-label="File preview">
        <Body kind={kind} text={text} wrap={wrap} truncated={more} />
        {more && (
          <button
            type="button"
            className="btn btn--solid text-viewer__more"
            onClick={() => setLimit((l) => l + PAGE)}
          >
            Show the next {formatBytes(PAGE)}
          </button>
        )}
      </div>
    </div>
  );
}

function Body({
  kind,
  text,
  wrap,
  truncated,
}: {
  kind: MediaKind;
  text: string;
  wrap: boolean;
  truncated: boolean;
}) {
  const md = useMemo(() => (kind === 'markdown' ? parseMarkdown(text) : null), [kind, text]);
  const csv = useMemo(() => (kind === 'csv' ? parseCsv(text) : null), [kind, text]);
  const json = useMemo(
    () => (kind === 'json' && !truncated ? prettyJson(text) : null),
    [kind, text, truncated],
  );
  const warnings = useMemo(() => (kind === 'xml' ? xmlWarnings(text) : []), [kind, text]);

  if (md)
    return (
      <div className="md">
        {md.map((b, i) => (
          <MdBlock key={i} b={b} />
        ))}
      </div>
    );
  if (csv) {
    const [head, ...rows] = csv.rows;
    return (
      <div className="csv">
        <table>
          <thead>
            <tr>
              {head?.map((c, i) => (
                <th key={i} scope="col">
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                {r.map((c, j) => (
                  <td key={j}>{c}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {csv.truncated && <p className="viewer__note">Showing the first {csv.rows.length} rows.</p>}
      </div>
    );
  }
  return (
    <>
      {warnings.map((w) => (
        <p key={w} className="viewer__note">
          {w}
        </p>
      ))}
      {json && !json.valid && (
        <p className="viewer__note">This is not valid JSON, so it is shown as plain text.</p>
      )}
      <pre className={`code ${wrap ? 'code--wrap' : ''}`}>{json ? json.text : text}</pre>
    </>
  );
}

function Inl({ nodes }: { nodes: Inline[] }) {
  return (
    <>
      {nodes.map((n, i) => {
        switch (n.t) {
          case 'text':
            return n.v;
          case 'code':
            return <code key={i}>{n.v}</code>;
          case 'em':
            return (
              <em key={i}>
                <Inl nodes={n.c} />
              </em>
            );
          case 'strong':
            return (
              <strong key={i}>
                <Inl nodes={n.c} />
              </strong>
            );
          case 'link':
            return (
              <a key={i} href={n.href} target="_blank" rel="noopener noreferrer nofollow">
                <Inl nodes={n.c} />
              </a>
            );
        }
      })}
    </>
  );
}

function MdBlock({ b }: { b: Block }) {
  switch (b.t) {
    case 'h': {
      const Tag = `h${Math.min(6, b.level + 1)}` as 'h2';
      return (
        <Tag>
          <Inl nodes={b.c} />
        </Tag>
      );
    }
    case 'p':
      return (
        <p>
          <Inl nodes={b.c} />
        </p>
      );
    case 'ul':
      return (
        <ul>
          {b.items.map((it, i) => (
            <li key={i}>
              <Inl nodes={it} />
            </li>
          ))}
        </ul>
      );
    case 'ol':
      return (
        <ol>
          {b.items.map((it, i) => (
            <li key={i}>
              <Inl nodes={it} />
            </li>
          ))}
        </ol>
      );
    case 'quote':
      return (
        <blockquote>
          <Inl nodes={b.c} />
        </blockquote>
      );
    case 'code':
      return (
        <pre className="code">
          <code>{b.v}</code>
        </pre>
      );
    case 'hr':
      return <hr />;
  }
}
